import { timestamp } from "../../lib/format";
import { energyKwh, orderMoney, paymentTone } from "../../lib/publicOrder";
import { money } from "../../lib/tariff";
import type { PublicOrderDetail } from "../../lib/types";
import { Disclosure } from "../ui/Disclosure";
import { Pill } from "../ui/Status";
import type { Column } from "../ui/Table";
import { Cell, TableFrame, TableHead, TableRow } from "../ui/Table";
import { Datum, MicroLabel } from "../ui/Text";

/**
 * The order's record, folded: every payment attempt, and the order's own fields.
 *
 * Both answer reconciliation questions — which attempt took the money, what
 * budget the driver bought — that matter when something is being disputed and not
 * otherwise. The closed summaries say enough to know whether to open them.
 */

const PAYMENT_COLUMNS: readonly Column[] = [
  { key: "occurred", label: "When", width: "22%" },
  { key: "status", label: "Status", width: "16%" },
  { key: "ref", label: "Gateway reference" },
  { key: "settle", label: "Reconciler", width: "18%" },
  { key: "amount", label: "Amount", numeric: true, width: "13%" },
];

export function OrderRecord({ row }: { readonly row: PublicOrderDetail }) {
  const zone = row.depotTimezone;
  const figures = orderMoney(row);

  return (
    <>
      <Disclosure
        label="Payments"
        summary={
          row.payments.length === 1 ? "1 attempt" : `${row.payments.length} attempts`
        }
      >
        {row.payments.length === 0 ? (
          <p className="m-0 max-w-prose text-body text-ink-muted">
            No payment attempt was ever recorded. A row appears the moment a checkout is opened,
            before the driver has decided anything.
          </p>
        ) : (
          // In full: after a retried settlement the newest row is not the one the
          // money came in on, and "which attempt took it" needs every row.
          <TableFrame>
            <TableHead columns={PAYMENT_COLUMNS} />
            <tbody>
              {row.payments.map((payment, index) => (
                <TableRow key={payment.id} index={index}>
                  <Cell>{timestamp(payment.occurredAt, zone)}</Cell>
                  <Cell>
                    <Pill tone={paymentTone(payment.status)}>{payment.status}</Pill>
                  </Cell>
                  <Cell muted className="break-all">
                    {payment.providerRef}
                    <span className="block text-micro tracking-normal normal-case text-ink-faint">
                      {payment.paymentOption ?? payment.provider}
                      {payment.approvalCode === null ? "" : ` · approval ${payment.approvalCode}`}
                    </span>
                  </Cell>
                  <Cell muted>{settleSummary(payment.settleState, payment.settleAttempts)}</Cell>
                  <Cell numeric>{money(payment.amountMinor, payment.currency)}</Cell>
                </TableRow>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Disclosure>

      <Disclosure
        label="Order details"
        summary={`${energyKwh(row.budgetWh)} kWh budget · ${row.idTag}`}
      >
        <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
          <Datum label={row.paymentFlow === "pre_auth" ? "Amount held" : "Amount bought"}>
            {money(row.amountMinor, row.currency)}
          </Datum>
          <Datum label="Budget">{energyKwh(row.budgetWh)} kWh</Datum>
          <Datum label="Unused">{figures.unused}</Datum>
          <Datum label="Already refunded">{money(row.refundedMinor, row.currency)}</Datum>
          <Datum label="Stop cause">{row.stopCause ?? "—"}</Datum>
          <Datum label="Refund reason">{row.refundReason ?? "—"}</Datum>
          {/* §4.1's synthetic tag. It is what the station saw, so it is the handle
              that joins this order to the OCPP message log. */}
          <Datum label="Identity tag" mono>
            {row.idTag}
          </Datum>
          <Datum label="Hold expires">{timestamp(row.holdExpiresAt, zone)}</Datum>
          <Datum label="Order id" mono>
            {row.id}
          </Datum>
        </dl>
        <MicroLabel>Times are shown in {zone}, the depot's zone.</MicroLabel>
      </Disclosure>
    </>
  );
}

/**
 * The reconciler's state, and how hard it had to work. The retry count is omitted
 * at zero: §9.4 counts *retries*, so "settled · 0 attempts" would say the
 * opposite of what happened.
 */
function settleSummary(state: string | null, attempts: number): string {
  if (state === null) return "—";
  if (attempts === 0) return state;

  return `${state} · ${attempts} retr${attempts === 1 ? "y" : "ies"}`;
}
